import orderBy from 'lodash-es/orderBy';
import { createSelector } from 'reselect';
import { IBusinessEntityMin } from '.';
import { IState } from '..';
import { IOption } from '../../components/Select';
import { IAddress } from '../address';
import { getContactName, IContact } from '../contact';
import { getStateAbbreviation } from '../lookups/lookupsSelectors';
import { ISalesCall } from '../salesCalls';

function getBusinessSalesCallIds(state: IState, businessEntityId: number): number[] {
  return state.businessEntity.salesCalls[businessEntityId] ? state.businessEntity.salesCalls[businessEntityId] : [];
}

export const getBusinessSalesCalls = createSelector(
  [getBusinessSalesCallIds, (state: IState) => state.salesCall.hash],
  (ids, hash) => orderBy(ids.map(id => hash[id]), (sc: ISalesCall) => sc.date, 'desc')
);

export const getBusinessTotalSalesCalls = (
  {
    businessEntity: {
      totalItems: { salesCalls }
    }
  }: IState,
  businessEntityId: number
): number => (salesCalls[businessEntityId] ? salesCalls[businessEntityId] : 0);

function getBusinessAddressIds(state: IState, businessEntityId: number): number[] {
  return state.businessEntity.addresses[businessEntityId] ? state.businessEntity.addresses[businessEntityId] : [];
}

export const getBusinessAddresses = createSelector(
  [getBusinessAddressIds, (state: IState) => state.address.hash, (state: IState) => state.lookups.states.hash],
  (ids, hash, stateHash) => {
    const addresses = ids.map(id => hash[id]);
    const grouped = addresses.reduce<{ primary: IAddress[]; secondary: IAddress[] }>(
      (obj, add) => {
        if (add.primary) {
          obj.primary = [...obj.primary, add];
        } else {
          obj.secondary = [...obj.secondary, add];
        }
        return obj;
      },
      {
        primary: [],
        secondary: []
      }
    );

    grouped.primary = orderBy(
      grouped.primary,
      (c: IAddress) =>
        `${c.city}, ${stateHash[c.stateId] ? stateHash[c.stateId].abbreviation : 'Unknown'}${
          c.street ? c.street.toLowerCase() : ''
        } ${c.street2 ? c.street2.toLowerCase() : ''}`
    );
    grouped.secondary = orderBy(
      grouped.secondary,
      (c: IAddress) =>
        `${c.city}, ${stateHash[c.stateId] ? stateHash[c.stateId].abbreviation : 'Unknown'}${
          c.street ? c.street.toLowerCase() : ''
        } ${c.street2 ? c.street2.toLowerCase() : ''}`
    );

    return [...grouped.primary, ...grouped.secondary];
  }
);

export function getBusinessEntityAddressOptions(state: IState, businessEntityId: number): IOption[] {
  const ids = state.businessEntity.addresses[businessEntityId] ? state.businessEntity.addresses[businessEntityId] : [];

  return ids
    .map<IAddress>(x => state.address.hash[x])
    .filter(x => x)
    .map<IOption>(x => ({
      label: `${x.primary ? 'Primary - ' : ''}${x.street}`,
      secondaryLabel: `${x.city}, ${getStateAbbreviation(state, x.stateId)} ${x.zipcode}`,
      value: x.addressId
    }));
}

function getBusinessContactIds(state: IState, businessEntityId: number): number[] {
  return state.businessEntity.contacts[businessEntityId] ? state.businessEntity.contacts[businessEntityId] : [];
}

export const getBusinessContacts = createSelector(
  [getBusinessContactIds, (state: IState) => state.contacts.hash],
  (ids, hash) =>
    orderBy(ids.map(id => hash[id]), (c: IContact) => `${c.lastName.toLowerCase()}, ${c.firstName.toLowerCase()}`)
);

export function contactsLoaded(state: IState, businessEntityId: number): boolean {
  return state.businessEntity.loaded.contacts.includes(businessEntityId);
}

export function salesCallsLoaded(state: IState, businessEntityId: number): boolean {
  return state.businessEntity.loaded.salesCalls.includes(businessEntityId);
}

export function getContactsLoading(state: IState, businessEntityId: number): boolean {
  return state.businessEntity.loading.contacts.includes(businessEntityId);
}

export function getSalesCallsLoading(state: IState, businessEntityId: number): boolean {
  return state.businessEntity.loading.salesCalls.includes(businessEntityId);
}

export const getActiveContactsAsOptions = createSelector(
  [getBusinessContactIds, (state: IState) => state.contacts.hash],
  (ids, hash) => {
    const contacts = ids.map(x => hash[x]).filter(x => !x.inactive);

    return orderBy(
      contacts.map<IOption>(
        x => ({ value: x.contactId, label: getContactName(x), secondaryLabel: x.title } as IOption)
      ),
      (o: IOption) => o.label
    );
  }
);

export function getSearchResults(state: IState, zipcode: string): IBusinessEntityMin[] {
  return state.businessEntity.search[zipcode] ? state.businessEntity.search[zipcode].items : [];
}

export function getBusinessEntityName(state: IState, businessEntityId: number): string {
  return state.businessEntity.names[businessEntityId] ? state.businessEntity.names[businessEntityId] : '';
}

export function getBusinessEntityCompetitors(state: IState, businessEntityId: number): number[] {
  return state.businessEntity.competitors[businessEntityId] ? state.businessEntity.competitors[businessEntityId] : [];
}

export function getBusinessEntityProductCategories(state: IState, businessEntityId: number): number[] {
  return state.businessEntity.productCategories[businessEntityId]
    ? state.businessEntity.productCategories[businessEntityId]
    : [];
}
